/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { send } from "src/utils/fetch";
import { captureException } from "src/utils/error";
import { createAppAsyncThunk } from "../async-thunk";
import { Serializer } from "../persistence";

export interface CurrentSubscriptionDataState {
  /**
   * Set to true only after a successful getVisitorSubscriptionStatus response.
   * When false/undefined, {@link hasActiveSubscription} may be stale initial or
   * rehydrated state and must not drive destructive billing actions.
   */
  subscriptionStatusLoaded?: boolean;
  hasActiveSubscription: boolean;
  subscriptionId?: string;
  isInTrialPeriod?: boolean;
  /**
   * This value is +1 compared to the value that we usually communicate to our users except for subscriptions with no trial
   * - no trial would have a value of 0
   * - a 7 days trial would have a value of 8
   * - a 14 days trial would have a value of 15
   */
  trialPeriodInDays?: number;
  planId?: string;
  nextChargeAmount?: number;
  expiryTime?: string;
  activeSubscriptionCurriculumSlug?: string;
  autoRenewalEnabled?: boolean;
  isIAPsubscription?: boolean;
  billingCycleInterval?: string;
  billingCycleIntervalCount?: number;
  accessCode?: string;
  batchId?: number;
  continuityOfCareChecked?: boolean;
  continuityOfCareEligible?: boolean;
}

const currentSubscriptionDataSlice = createSlice({
  name: "currentSubscriptionData",
  initialState: {
    hasActiveSubscription: false,
    continuityOfCareChecked: false,
    continuityOfCareEligible: false,
  } as CurrentSubscriptionDataState,
  reducers: {
    setCurrentSubscriptionData(
      state,
      action: PayloadAction<CurrentSubscriptionDataState>
    ) {
      // Preserve frontend-only fields that aren't included in API responses
      return {
        ...action.payload,
        continuityOfCareChecked: state.continuityOfCareChecked,
        continuityOfCareEligible: state.continuityOfCareEligible,
      };
    },
    setContinuityOfCareChecked(state, action: PayloadAction<boolean>) {
      state.continuityOfCareChecked = action.payload;
    },
    setContinuityOfCareEligible(state, action: PayloadAction<boolean>) {
      state.continuityOfCareEligible = action.payload;
    },
  },
});

export const {
  setCurrentSubscriptionData,
  setContinuityOfCareChecked,
  setContinuityOfCareEligible,
} = currentSubscriptionDataSlice.actions;

export default currentSubscriptionDataSlice;

const memo: Record<string, Promise<CurrentSubscriptionDataState>> = {};

/**
 * Fetches the active subscription data of an user using the email
 * TODO: Email parameter can be removed since the endpoint uses _userId identification
 */
export const loadActiveSubscriptionDataState = createAppAsyncThunk(
  "currentSubscriptionData/loadActiveSubscriptionDataState",
  async ({ email }: { email?: string }, thunkAPI) => {
    if (!memo[email]) {
      const params = new URLSearchParams();
      if (email) {
        params.set("email", email);
      }

      const url = `/visitor/api/v1/getVisitorSubscriptionStatus/?${params}`;

      let currentSubscriptionData: CurrentSubscriptionDataState = {
        hasActiveSubscription: false,
      };
      let loadSucceeded = false;

      memo[email] = send("GET", url)
        .then((data) => {
          currentSubscriptionData = data;
          loadSucceeded = true;
        })
        .catch((e) => {
          captureException(e, "loadActiveSubscriptionDataState");

          // Delete from memo if API call fails so we can retry
          delete memo[email];
        })
        .then(() => {
          if (loadSucceeded) {
            thunkAPI.dispatch(
              setCurrentSubscriptionData({
                ...currentSubscriptionData,
                subscriptionStatusLoaded: true,
              })
            );
          }
          return currentSubscriptionData;
        });
    }
    return memo[email];
  }
);

export const serialize: Serializer<"currentSubscriptionData"> = {
  storage: "local",
  save: (state: CurrentSubscriptionDataState) => {
    return state;
  },
  load: (storedState: CurrentSubscriptionDataState) => {
    return (
      storedState || {
        hasActiveSubscription: false,
        continuityOfCareChecked: false,
        continuityOfCareEligible: false,
      }
    );
  },
};
